import moment from 'moment';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { PlantItemConstants } from 'plantItemNew/constants/plantItemConstants';
import { PlantDesignConstants } from 'plantDesign/constants/plantDesignConstants';
import { PLANT_API_BASE_URL } from 'shared/constants/constants';

const sanitizeHtml = (dirtyHTML) => {
  const cleanHTML = DOMPurify.sanitize(dirtyHTML, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target'],
  });

  return parse(cleanHTML);
};

const dateOfBirthStr = (poi) => {
  let poiData = poi.poiData;
  let dateofbirthValue = '';

  if (poiData) {
    let documents = poiData.documents;
    for (var document in documents) {
      let docData = documents[document].data;

      if (docData['birthDate'] && docData['birthDate'] !== '') {
        dateofbirthValue = docData['birthDate'];

        break;
      }
    }
  }

  return moment(dateofbirthValue, 'DD-MM-YYYY').format('YYYY-MM-DD');
};

const calOneWorkHours = ({ startDate, endDate, experienceType, partTimehours }) => {
  const sDate = new Date(startDate);
  const eDate = new Date(endDate);
  const diffDays = (eDate.getTime() - sDate.getTime()) / (1000 * 3600 * 24) + 1; // Include the end date day also
  let totalweeks = Math.round(diffDays / 7) > diffDays / 7 ? Math.round(diffDays / 7) - 1 : Math.round(diffDays / 7);
  let remainingDays = diffDays % 7;

  let workHours;
  if (experienceType === 'full-time') {
    workHours = totalweeks * 35 + remainingDays * 7;
  } else {
    let hoursPerDay = Math.round(partTimehours / 5);
    workHours = totalweeks * partTimehours + remainingDays * hoursPerDay;
  }

  return workHours;
};

const getFullname = (fname, mname, lname) => {
  let fullname = '';

  if (fname) {
    fullname += fname + ' ';
  }
  if (mname) {
    fullname += mname + ' ';
  }

  if (lname) {
    fullname += lname;
  }

  return fullname;
};

const formatAddress = (address) => {
  if (address && address.enterManually && !address.international) {
    return `${address.addressLine}, ${address.suburb}, ${address.state.text}, ${address.postcode}`;
  } else if (address && address.lookupValue && address.lookupValue.label) {
    return address.lookupValue.label;
  } else {
    return `${address.addressLine}, ${address.suburb}, ${address.state ? address.state + ',' : ''} ${
      address.postcode
    }, ${address.country}`;
  }
};

const sanitiseAddress = (address) => {
  //if manual address, make sure max address is 40 characters
  if (!address || !address?.enterManually) return address;

  if (address.addressLine.length <= 40) return address;

  return { ...address, addressLine: address.addressLine.substring(40, 0) };
};

const mapRefDataPickList = (pickList, valueValName = 'value', textValName = 'description') => {
  if (!Array.isArray(pickList)) return [];

  return pickList.map((e) => ({
    value: e[valueValName],
    label: e[textValName],
    text: e[textValName],
    ...(e['description2'] && { condition: e['description2'] }),
  }));
};

const selectedTextFromOptions = (options, selectedValue) => {
  if (!Array.isArray(options) || !selectedValue) return '';

  const typeDescription = options.find((element) => element.value === selectedValue);
  return typeDescription?.text;
};

const normaliseName = (firstName = '', middleName = '', lastName = '') => {
  return `${firstName}${middleName}${lastName}`.toLocaleLowerCase().replace(/\s/g, '');
};

const getAttachmentApi = (applicationId) => {
  const attachmentEndpointUrl = `${PLANT_API_BASE_URL}/application/${applicationId}/attachments`;
  return {
    uploadFile: async (file) => {
      const content = new FormData();
      content.append('file', file);
      const options = {
        url: `${attachmentEndpointUrl}/${file.name}`,
        method: 'post',
        data: content,
      };
      const response = await axios(options);
      return response.data.toString();
    },
    removeFile: async (fileId) => {
      const response = await axios.delete(`${attachmentEndpointUrl}/${fileId}`);
      return response.data;
    },
  };
};

const capitalizeFirstLetter = (str) => {
  if (!str) return str;

  return str.charAt(0).toUpperCase() + str.slice(1);
};

const setNativeValue = (element, value) => {
  const valueSetter = Object.getOwnPropertyDescriptor(element, 'value').set;
  const prototype = Object.getPrototypeOf(element);
  const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value').set;

  if (valueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter.call(element, value);
  } else {
    valueSetter.call(element, value);
  }
};

const getPlantItemlLicenceTypeCode = (licenceTypes) => {
  if (!licenceTypes?.data) return null;

  return licenceTypes.data.find((item) => item.description === PlantItemConstants.licenceTypeDescription).code;
};

const getPlantDesignlLicenceTypeCode = (licenceTypes) => {
  if (!licenceTypes?.data) return null;

  return licenceTypes.data.find((item) => item.description === PlantDesignConstants.licenceTypeDescription).code;
};

const splitFullName = (fullName) => {
  if (!fullName) {
    console.error('Full name is null.');
  }

  const names = fullName.split(' ');

  // As of now, these first name & last name are only for declaration in BE where they will be concatenated back anyway,
  // so there are no spefic rules required for this split.
  return {
    firstName: names.shift(), // First element
    lastName: names.length ? names.join(' ') : '', // Rest of the elements
  };
};

export {
  dateOfBirthStr,
  getFullname,
  formatAddress,
  calOneWorkHours,
  sanitizeHtml,
  sanitiseAddress,
  mapRefDataPickList,
  normaliseName,
  getAttachmentApi,
  capitalizeFirstLetter,
  selectedTextFromOptions,
  setNativeValue,
  getPlantItemlLicenceTypeCode,
  getPlantDesignlLicenceTypeCode,
  splitFullName,
};
